import React from 'react'
import PropTypes from 'prop-types'
import Router from 'next/router'
import { TaxRegistrationStore, SessionStore } from '../../stores'
import { inject, observer } from 'mobx-react'
import { ConfirmBox } from '../confirm-box/confirm'
import { Placeholder } from '../placeholder'
import dayjs from 'dayjs'
import Http from '../../services/http'
import { dataSources } from '../../utils/data-sources'
import { Companies } from './companies'
import { publish } from '../../utils/pubsub'
import { PortalContainer } from '../../components/portal-container'
import { handleErrorsAPI } from '../handle-errors'
import { DangerAlert } from '../../components/alerts/danger'
import { trimmedSpaces } from './../../../ui/services/trimmedSpaces'
import { getFraudHeaders } from '../../utils/get-fraud-headers'
import { validateRegistrationNumber } from '../../utils/validate-registration-number'
import { loadTaxRegistrationsData } from '../../contents/configuration/tax-registrations/load-data'

const textInvalid = 'invalid-text'
const dateFormat = 'YYYY-MM-DD'
const constantObject = {
  configuration: '/configuration/tax-registrations',
  danger: 'toastr.danger',
  yearFormat: 'YYYY-MM-DD'
}

const dateFilters = {
  oneMonthBefore: dayjs().subtract(1, 'month').format(constantObject.yearFormat),
  threeMonthsBefore: dayjs().subtract(3, 'month').format(constantObject.yearFormat),
  sixMonthsBefore: dayjs().subtract(6, 'month').format(constantObject.yearFormat),
  twelveMonthsBefore: dayjs().subtract(12, 'month').format(constantObject.yearFormat)
}

@inject('taxRegistrationStore', 'sessionStore')
@observer
class TaxRegistrationForm extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      ready: false,
      valid: false,
      enableSave: true,
      displayMsg: '',
      displaySubmissionGatewaySettingsConfirm: true,
      formData: {
        id: null,
        registrationNumber: '',
        registeredName: '',
        registeredAddress: '',
        requiresApproval: false,
        accountingScheme: '',
        active: false,
        SubmissionGatewayId: '', // must be capitalised for back-end,
        submissionGatewaySettings: {
          startDate: undefined,
          frequency: undefined
        },
        Companies: []
      },
      selectedSubmissionGateway: null,
      submissionGateways: [],
      deleteTaxRegistrationShowConfirm: false,
      isLoading: false,
      importError: false,
      companyError: false,
      taxRegistrationEror: false,
      errorImportMsg: null,
      errorCompanyMsg: null
    }
    this.formRef = React.createRef()
    this.saveSubmitButton = React.createRef()
    this.http = Http.getInstance(this.props.sessionStore)
    this.dataSources = dataSources()
    this.cancelButtonClick = this.cancelButtonClick.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.handleSubmissionGatewayChange = this.handleSubmissionGatewayChange.bind(this)
    this.handleSubmissionGatewaySettingsChange = this.handleSubmissionGatewaySettingsChange.bind(this)
    this.handleTaxRegError = this.handleTaxRegError.bind(this)
    this.saveData = this.saveData.bind(this)
    this.deleteButtonClick = this.deleteButtonClick.bind(this)
    this.deleteTaxRegistration = this.deleteTaxRegistration.bind(this)
    this.handleCompanyChange = this.handleCompanyChange.bind(this)
    this.submissionGatewayStartDate = React.createRef()
    this.submissionGatewayRef = React.createRef()
    this.registrationNumberRef = React.createRef()
    this.registerNameRef = React.createRef()
    this.registerAddressRef = React.createRef()
    this.accountingSchemeRef = React.createRef()
    this.statusToggleRef = React.createRef()
    this.obligationFrequencyRef = React.createRef()
    this.deleteDialogRef = React.createRef()
  }

  loadData () {
    return this.http.getData(this.dataSources.submissionGateway)
  }

  handleAddTaxReg = (newState) => {
  // If we are creating a new tax registration
    if (this.props.addTaxReg) {
    // consultant mode check is on, then it will only provide option to select the test or mock gateway api
    // if off then will allow to create only live api gateway
      if (this.props.sessionStore.consultantModeCheck) {
        newState.submissionGateways = newState.submissionGateways.filter(s => !s.online)
      } else {
        newState.submissionGateways = newState.submissionGateways.filter(s => s.online)
      }
    }
  }

  handleEditRegDetails = (newState) => {
    const currentTaxRegistration = this.props.taxRegistrationStore.currentTaxRegistration
    const fetchUrlId = window.location.href.split('?')[1].split('=')[1]
    if (fetchUrlId !== currentTaxRegistration.id) {
      publish(constantObject.danger, 'Failed to load data')
      setTimeout(() => {
        Router.push('/configuration/tax-registrations')
      }, 2000)
    } else {
      newState.selectedSubmissionGateway = newState.submissionGateways.find(g => g.id === currentTaxRegistration.SubmissionGatewayId)
      // if already selected a real api then user cannot select test or mock api from dropdown
      // So removed the offline items from the list
      if (newState.selectedSubmissionGateway.online) {
        newState.submissionGateways = newState.submissionGateways.filter(s => s.online)
      } else {
        // consultant mode check is on, then it will only provide option to select the test or mock gateway api
        // if off then will allow to create only live api gateway
        newState.submissionGateways = newState.submissionGateways.filter(s => !s.online)
      }
      newState.formData = {
        ...newState.formData,
        ...currentTaxRegistration,
        submissionGatewaySettings: {
          startDate: (currentTaxRegistration.submissionGatewaySettings) ? dayjs(currentTaxRegistration.submissionGatewaySettings.startDate).format(dateFormat) : undefined,
          frequency: (currentTaxRegistration.submissionGatewaySettings) ? currentTaxRegistration.submissionGatewaySettings.frequency : undefined
        }
      }
    }
  }

  componentDidMount () {
    if (this.props.sessionStore.isAdmin || this.props.sessionStore.isApprover) {
      const newState = {
        ready: true
      }
      this.loadData()
        .then((viewData) => {
          newState.submissionGateways = viewData.data
          // If we are updating the existing tax registration
          if (this.props.taxRegistrationStore.currentTaxRegistration) {
            this.handleEditRegDetails(newState)
          }
          this.handleAddTaxReg(newState)
          this.setState(newState, () => {
            this.setup()
            this.deleteModalEvents()
          })
        })
        .catch(() => {
          this.setState(newState, () => { this.setup() })
        })
    } else {
      Router.push('/unauthorised')
    }
  }

  deleteButtonClick () {
    this.deleteDialogRef.current.show()
  }

  deleteTaxRegistration () {
    this.deleteDialogRef.current.close()
    this.http.deleteData(`${this.dataSources.taxRegistrations}/${this.state.formData.id}`).then(response => {
      if (response.status === 'success') {
        this.setState({
          isLoading: false,
          enableSave: false
        })
        if (this.props.taxRegistrationStore.taxRegistrationList) {
          const newTaxRegistrationList = this.props.taxRegistrationStore.taxRegistrationList.slice().filter(item => item.id !== this.state.formData.id)
          this.props.taxRegistrationStore.setTaxRegistrationList(newTaxRegistrationList)
        }
        Router.push(constantObject.configuration)
      } else if (response.data.status === 'fail') {
        publish(constantObject.danger, response.data.data)
        this.setState({
          isLoading: false
        })
      }
    }).catch(err => {
      console.error(err)
      publish(constantObject.danger, 'An error occurred deleting the tax registration')
      this.setState({
        isLoading: false
      })
    })
  }

  handleCompanyChange (fetchedCompany) {
    this.setState({
      formData: {
        ...this.state.formData,
        Companies: fetchedCompany
      }
    }, () => {
      setTimeout(() => {
        if (this.submissionGatewayStartDate.current) {
          this.performDateFilteration()
        }
        this.validateRegistrationNumberModulus()
      }, 100)
    })
  }

  validateRegistrationNumberModulus () {
    const registrationNumber = this.state.formData.registrationNumber

    const invalidMessage = 'Please enter a valid registration number. This should be 9 digits long and confirm to HMRC rules.'

    if (this.registrationNumberRef.current) {
      this.registrationNumberRef.current.removeAttribute('invalid')
      this.registrationNumberRef.current.removeAttribute(textInvalid)
    }
    if (this.registrationNumberRef.current && registrationNumber.length !== 9) {
      this.registrationNumberRef.current.invalid = true
      this.registrationNumberRef.current.invalidText = invalidMessage
      return invalidMessage
    }

    const selectedSubmissionGateway = this.state.submissionGateways.find(g => g.id === this.state.formData.SubmissionGatewayId)
    const requiresAuthentication = (selectedSubmissionGateway && selectedSubmissionGateway.online === true)
    // We only do validate the registration number if we are dealing with a online gateway, if offline or not yet specified then stop
    if (this.state.enableOfflineOptions === null || requiresAuthentication === false) {
      return undefined
    }

    const valid = validateRegistrationNumber(registrationNumber)
    if (valid) {
      return undefined
    }

    // We set the control appropriately
    if (this.registrationNumberRef.current) {
      this.registrationNumberRef.current.invalid = true
      this.registrationNumberRef.current.invalidText = invalidMessage
    }

    return invalidMessage
  }

  /* form handling methods */

  handleChange (e) {
    const formData = { ...this.state.formData }
    formData[e.target.name] = e.target.value
    this.setState({
      formData
    }, () => {
      setTimeout(() => {
      // modulus validation check against registration number

        const displayError = this.validateRegistrationNumberModulus()
        if (displayError || this.submissionGatewayStartDate.current?.invalid) {
          this.setState({ enableSave: false })
        } else {
          this.registrationNumberRef.current.removeAttribute('invalid')
          this.setState({ enableSave: true })
        }
      }, 100)
    })
  }

  handleSubmissionGatewayChange (e) {
    const formData = { ...this.state.formData }
    formData[e.target.name] = e.target.value
    // when changed set the values to empty or default null
    formData.submissionGatewaySettings = {
      startDate: this.state.formData.submissionGatewaySettings.startDate,
      frequency: this.state.formData.submissionGatewaySettings.frequency
    }

    this.setState({
      ready: false
    }, () => {
      this.setState({
        ready: true,
        formData,
        selectedSubmissionGateway: this.state.submissionGateways.find(g => g.id === formData.SubmissionGatewayId),
        enableSave: true
      }, () => {
        setTimeout(() => {
          const displayError = this.validateRegistrationNumberModulus()
          if (displayError) {
            this.setState({ enableSave: false }, () => this.setup())
          } else {
            this.setup()
          }
        }, 100)
      })
    })
  }

  dealErrorMessage = () => {
    const messageDis = 'The obligation end date should be less than current date. Select the obligation start date appropriately'
    this.submissionGatewayStartDate.current.invalid = true
    this.submissionGatewayStartDate.current.invalidText = messageDis
    this.setState({ enableSave: false })
  }

  successfulUpdate = () => {
    this.submissionGatewayStartDate.current.removeAttribute('invalid')
    this.submissionGatewayStartDate.current.removeAttribute(textInvalid)
  }

  setFrequencyChangeConfirmBox = (e) => {
    this.setState({
      updatedData: e,
      showFrequencyConfirm: true
    }, () => {
      this.setup()
    })
  }

  toggleFrequencyChangeConfirm = () => {
    this.setState({ showFrequencyConfirm: !this.state.showFrequencyConfirm })
  }

  rejectFrequencyChangeConfirm = () => {
    const { submissionGatewaySettings: { startDate, frequency } } = this.props.taxRegistrationStore.currentTaxRegistration
    // restore back the previous data in the tax registration page
    this.setState({
      showFrequencyConfirm: false
    }, () => {
      const formData = { ...this.state.formData }

      formData.submissionGatewaySettings.frequency = frequency

      formData.submissionGatewaySettings.startDate = startDate
      this.setState({
        ready: false,
        formData: {}
      }, () => {
        this.setState({
          ready: true,
          formData,
          enableSave: true
        }, () => setTimeout(() => {
          this.submissionGatewayStartDate.current.close()
          this.validateRegistrationNumberModulus()
          this.setup()
        }, 100))
      })
    })
  }

  handleSubmissionGatewaySettingsChange (e) {
    if (e.target.name) {
      const formData = { ...this.state.formData }
      formData.submissionGatewaySettings[e.target.name] = e.target.name === 'startDate' ? dayjs(e.detail.dateStr).format(dateFormat) : e.target.value
      this.setState({
        ready: false
      }, () => {
        this.setState({
          ready: true,
          formData,
          enableSave: true
        }, () => {
          if (!this.props.addTaxReg && (e.target.name === 'frequency' || e.target.name === 'startDate')) {
            this.setFrequencyChangeConfirmBox(e)
          }
          setTimeout(() => {
            this.performDateFilteration()
            this.validateRegistrationNumberModulus()
            this.setup()
          }, 100)
        }
        )
      })
    } else {
      setTimeout(() => {
        this.performDateFilteration()
        this.setup()
      }, 100)
    }
  }

  /* tax registration button handling methods */

  cancelButtonClick () {
    if (window.confirm('Changes you made may not be saved. Do you want to continue?')) {
      Router.push(constantObject.configuration)
    }
  }

  handleSubmit = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const getReg = document.getElementById('registrationNumber')
    const getClassFromReg = getReg.querySelector('.valid-feedback')
    if (getClassFromReg) {
      getReg.removeChild(getClassFromReg)
    }
    const messageDis = 'Obligation start date required'
    if (this.submissionGatewayStartDate.current) {
      if (this.submissionGatewayStartDate.current.value.length === 0) {
        this.submissionGatewayStartDate.current.invalid = true
        this.submissionGatewayStartDate.current.invalidText = messageDis
        return false
      }
    }
    if (this.state.enableSave) {
      if (this.state.valid) {
        return
      }
      if (this.state.formData.Companies.length === 0) {
        publish(constantObject.danger, 'At least one company must exist')
      } else {
        this.setState({
          valid: true
        }, () => this.saveData(e))
      }
    }
  }

  handleErrorSubmit = (e, formData, errorInputs) => {
    e.preventDefault()
    e.stopPropagation()
  }

  formatCompanyData (company) {
    let glData
    const GLElementData = []
    company.numberGLElements = (company.numberGLElements === '' || company.numberGLElements === null) ? 0 : company.numberGLElements
    if (company.numberGLElements !== 0) {
      company.GLElements.map(data => {
        if (data.id) {
          glData = {
            id: data.id,
            glNumber: parseInt(data.glNumber),
            label: data.label,
            numberCharacters: parseInt(data.numberCharacters)
          }
        } else {
          glData = {
            glNumber: parseInt(data.glNumber),
            label: data.label,
            numberCharacters: parseInt(data.numberCharacters)
          }
        }
        GLElementData.push(glData)
      })
    }
    if (company.id) {
      return {
        id: company.id,
        name: company.name,
        sourceReference: company.sourceReference,
        active: company.active,
        numberImportParts: parseInt(company.numberImportParts),
        numberGLElements: parseInt(company.numberGLElements),
        ImportParts: company.ImportParts.map(data => {
          if (data.id) {
            return {
              id: data.id,
              importPart: data.importPart,
              type: data.type,
              active: data.active
            }
          } else {
            return {
              importPart: data.importPart,
              type: data.type,
              active: data.active
            }
          }
        }),
        GLElements: GLElementData
      }
    } else {
      return {
        name: company.name,
        sourceReference: company.sourceReference,
        numberImportParts: parseInt(company.numberImportParts),
        active: company.active,
        numberGLElements: parseInt(company.numberGLElements),
        ImportParts: company.ImportParts.map(data => {
          return {
            importPart: data.importPart,
            type: data.type,
            active: data.active
          }
        }),
        GLElements: GLElementData
      }
    }
  }

  handleBaseEdition = (c) => {
    if (c.id) {
      return {
        id: c.id,
        name: c.name,
        sourceReference: c.sourceReference,
        active: c.active
      }
    } else {
      return {
        name: c.name,
        sourceReference: c.sourceReference,
        active: c.active
      }
    }
  }

  handleInactiveCompanies = (errorContentDisplay) => {
    let companyInactiveError1, companyInactiveError2, importInactive1, importInactive2
    const inactiveCompanies = []
    const inactiveImports = []
    for (const i of errorContentDisplay) {
      for (let j = 0; j < i.length; j++) {
        const errorDisplay = i
        if (errorDisplay[j].status === 'error') {
          if (errorDisplay[j].message[0].includes('importpart')) {
            this.setState({
              importError: true
            })
            inactiveImports.push(errorDisplay[j].name)
            importInactive1 = errorDisplay[j].message[0].slice(0, 11)
            importInactive2 = errorDisplay[j].message[0].slice(15)
          }

          if (errorDisplay[j].message[0].includes('company')) {
            this.setState({
              companyError: true
            })
            inactiveCompanies.push(errorDisplay[j].name)
            companyInactiveError1 = errorDisplay[j].message[0].slice(0, 11)
            companyInactiveError2 = errorDisplay[j].message[0].slice(23)
          }
        }
      }
    }
    return {
      companyInactiveError1, companyInactiveError2, importInactive1, importInactive2, inactiveCompanies, inactiveImports
    }
  }

  handleTaxRegistrationInvalidations = (companyInactiveError1, companyInactiveError2, importInactive1, importInactive2, inactiveCompanies, inactiveCompanyNames, inactiveImportsCoNames) => {
    if (this.state.companyError) {
      if (inactiveCompanies.length > 0) {
        this.setState({
          isLoading: false,
          valid: false,
          errorCompanyMsg: `${companyInactiveError1} ${inactiveCompanyNames} ${companyInactiveError2} `
        })
      }
    }

    if (this.state.importError) {
      if (inactiveImportsCoNames.length > 0) {
        this.setState({
          isLoading: false,
          valid: false,
          errorImportMsg: `${importInactive1} ${inactiveImportsCoNames} ${importInactive2} `
        })
      }
    }

    if (this.state.taxRegistrationEror) {
      this.setState({
        isLoading: false,
        valid: false
      })
      publish(constantObject.danger, this.state.errMsg)
    }
  }

  handleRegistrationSuccess = (updateData) => {
    if (!this.state.companyError && !this.state.importError && !this.state.taxRegistrationEror) {
      publish('toastr.success', 'Tax registration successfully updated')
      this.setState({
        isLoading: false,
        valid: false,
        enableSave: false
      })
      if (this.props.taxRegistrationStore.taxRegistrationList) {
        const newTaxRegistrationList = this.props.taxRegistrationStore.taxRegistrationList.slice().filter(item => item.id !== this.state.formData.id)
        updateData.id = this.state.formData.id
        updateData.SubmissionGateway = this.state.selectedSubmissionGateway
        updateData.SubmissionGatewayName = updateData.SubmissionGateway.name
        updateData.SubmissionGatewayOnline = updateData.SubmissionGateway.online
        newTaxRegistrationList.splice(0, 0, updateData)
        this.props.taxRegistrationStore.setTaxRegistrationList(newTaxRegistrationList)
      }
      Router.push(constantObject.configuration)
    }
  }

  handleSuccess = (response, updateData) => {
    const errorContentDisplay = response.data

    const getInactiveCompValues = this.handleInactiveCompanies(errorContentDisplay)
    const { companyInactiveError1, companyInactiveError2, importInactive1, importInactive2, inactiveCompanies, inactiveImports } = getInactiveCompValues
    for (const i of errorContentDisplay) {
      if (i.status === 'error') {
        if (i.message[0].includes('tax registration')) {
          this.setState({
            taxRegistrationEror: true,
            errMsg: i.message[0]
          })
        }
      }
    }

    const uniqueImportArray = inactiveImports.filter(function (item, pos) {
      return inactiveImports.indexOf(item) === pos
    })

    const inactiveCompanyNames = inactiveCompanies.toString()
    const inactiveImportsCoNames = uniqueImportArray.toString()
    this.handleTaxRegistrationInvalidations(companyInactiveError1, companyInactiveError2, importInactive1, importInactive2, inactiveCompanies, inactiveCompanyNames, inactiveImportsCoNames)
    this.handleRegistrationSuccess(updateData)
  }

  handleExistingFormPost = async (updateData) => {
    const fraudHeaders = await getFraudHeaders(this.props.sessionStore)
    this.http.putData(`${this.dataSources.taxRegistrations}/${this.state.formData.id}`, updateData, {
      'headers': fraudHeaders
    }).then(response => {
      if (response.status === 'success') {
        this.handleSuccess(response, updateData)
      } else {
        this.handleTaxRegError(response)
      }
      this.setState({
        isLoading: false,
        valid: false
      })
    }).catch(err => {
      console.error(err)
      publish(constantObject.danger, 'An error occurred updating the tax registration')
      this.setState({
        isLoading: false,
        valid: false
      })
    })
  }

  handleNewFormPost = (updateData) => {
    getFraudHeaders(this.props.sessionStore)
      .then((fraudHeaders) => {
        return this.http.postData(this.dataSources.taxRegistrations, updateData, {
          'headers': fraudHeaders
        })
      })
      .then(response => {
        if (response.status === 'success') {
          this.handleSuccessResponse(response, updateData)
        } else {
          this.handleTaxRegError(response)
        }
        this.setState({
          isLoading: false,
          valid: false
        })
      }).catch(err => {
        console.error(err)
        publish(constantObject.danger, 'An error occurred saving the tax registration')
        this.setState({
          isLoading: false,
          valid: false
        })
      })
  }

  createInitialTaxRegObj = () => {
    return {
      registrationNumber: this.state.formData.registrationNumber,
      registeredName: this.state.formData.registeredName,
      registeredAddress: this.state.formData.registeredAddress,
      requiresApproval: this.state.formData.requiresApproval,
      accountingScheme: this.state.formData.accountingScheme || '',
      active: this.state.formData.active,
      SubmissionGatewayId: this.state.formData.SubmissionGatewayId, // must be capitalised for back-end,
      submissionGatewaySettings: this.state.formData.submissionGatewaySettings
    }
  }

  saveData = (e) => {
    const takeRegName = trimmedSpaces(this.state.formData.registeredName)
    const takeRegAddress = trimmedSpaces(this.state.formData.registeredAddress)
    const takeAccountingScheme = trimmedSpaces(this.state.formData.accountingScheme)
    this.setState({ formData: { ...this.state.formData, accountingScheme: takeAccountingScheme, registeredName: takeRegName, registeredAddress: takeRegAddress } })
    const updateData = this.createInitialTaxRegObj()
    if (this.state.valid) {
      this.setState({ isLoading: true, importError: false, companyError: false, taxRegistrationEror: false }, () => {
        if (this.state.formData.id) {
          if (this.props.sessionStore.isFullEdition) {
            updateData.Companies = this.state.formData.Companies.map(c => {
              return this.formatCompanyData(c)
            })
          } else {
            updateData.Companies = this.state.formData.Companies.map(c => {
              return this.handleBaseEdition(c)
            })
          }
          this.handleExistingFormPost(updateData)
        } else {
          if (this.props.sessionStore.isFullEdition) {
            updateData.Companies = this.state.formData.Companies.map(c => {
              return this.formatCompanyData(c)
            })
          } else {
            updateData.Companies = this.state.formData.Companies.map(c => {
              return {
                name: c.name,
                sourceReference: c.sourceReference,
                active: c.active
              }
            })
          }
          this.handleNewFormPost(updateData)
        }
      })
    }
  }

  handleSuccessResponse = (response, updateData) => {
    publish('toastr.success', 'Tax registration successfully saved')
    this.setState({
      isLoading: false,
      valid: false,
      enableSave: false
    })
    updateData.id = response.data.id
    updateData.SubmissionGateway = this.state.selectedSubmissionGateway
    updateData.SubmissionGatewayName = updateData.SubmissionGateway.name
    updateData.SubmissionGatewayOnline = updateData.SubmissionGateway.online

    if (this.props.taxRegistrationStore.taxRegistrationList) {
      const newTaxRegistrationList = this.props.taxRegistrationStore.taxRegistrationList.slice()
      newTaxRegistrationList.splice(0, 0, updateData)
      this.props.taxRegistrationStore.setTaxRegistrationList(newTaxRegistrationList)
    }
    this.props.taxRegistrationStore.setCurrentTaxRegistration(updateData)
    Router.push(constantObject.configuration)
  }

  handleTaxRegError (response) {
    if (response && response.data && response.data.message &&
      response.data.message.match(/The tax registration number cannot be verified against the authorised HMRC account/i)) {
      const message = response.data.message
      publish(constantObject.danger, {
        message: message,
        options: {
          closeButton: true,
          showAnimation: 'animated fadeIn',
          hideAnimation: 'animated fadeOut',
          timeOut: 0,
          extendedTimeOut: 0,
          style: {
            width: '500px'
          }
        }
      })
    } else {
      loadTaxRegistrationsData(this.props.sessionStore, this.props.taxRegistrationStore).then(() => handleErrorsAPI(response, this.props.sessionStore, this.props.env))
    }
  }

  performDateFilteration = () => {
    // checking if the given obligation date is less current date based on the selected frequencies
    const formData = { ...this.state.formData }
    const { frequency, startDate } = formData.submissionGatewaySettings
    if (frequency === 'month' && startDate >= dateFilters.oneMonthBefore) {
      this.dealErrorMessage()
    } else if (frequency === 'quarter' && startDate >= dateFilters.threeMonthsBefore) {
      this.dealErrorMessage()
    } else if (frequency === 'biyear' && startDate >= dateFilters.sixMonthsBefore) {
      this.dealErrorMessage()
    } else if (frequency === 'year' && startDate >= dateFilters.twelveMonthsBefore) {
      this.dealErrorMessage()
    } else {
      this.successfulUpdate()
    }
  }

  acceptFrequencyChangeConfirm = () => {
    // update the frequency and obligation so that if any tax return or other details exist it gets removed
    this.setState({
      showFrequencyConfirm: false,
      displaySubmissionGatewaySettingsConfirm: false
    }, () => {
      setTimeout(() => {
        const formData = { ...this.state.formData }
        if (this.state.updatedData.target) {
          formData.submissionGatewaySettings[this.state.updatedData.target.name] = this.state.updatedData.target.name === 'startDate'
            ? this.state.updatedData.detail.dateStr : this.state.updatedData.target.value
        }
        this.performDateFilteration()
        this.submissionGatewayStartDate.current.close()
        this.setup()
      }, 350)
    })
  }

  getHeaderButton () {
    return (
      <PortalContainer parentContainer='btn-container'>
        {this.getHeaderTopButton()}
      </PortalContainer>
    )
  }

  getHeaderTopButton () {
    if (this.props.addTaxReg) {
      return (
        <mosaic-button-group spaced class='float-right'>
          <mosaic-button
            type='button'
            icon='cancel'
            color='secondary'
            onClick={this.cancelButtonClick}
            className='mr-1'>
            Cancel
          </mosaic-button>
        </mosaic-button-group>
      )
    } else {
      return (
        <mosaic-button-group spaced class='float-right'>
          <mosaic-button
            type='button'
            icon='cancel'
            color='secondary'
            onClick={this.cancelButtonClick}
            className='mr-1'>
            Cancel
          </mosaic-button>
          <mosaic-button
            type='button'
            color='danger'
            icon='delete_sign'
            onClick={this.deleteButtonClick}>
            Delete
          </mosaic-button>
        </mosaic-button-group>
      )
    }
  }

  getButtons () {
    if (this.props.addTaxReg) {
      return (
        <mosaic-button-group spaced class='float-right'>
          <div className='button-alignment'>

            <mosaic-button
              type='button'
              icon='cancel'
              color='secondary'
              onClick={this.cancelButtonClick}
              className='mr-1'>
            Cancel
            </mosaic-button>
            <mosaic-button
              icon='save'
              color='success'
              type='submit'
              className='ml-1'
              submit
              spinner
              disabled={(this.state.isLoading || !this.state.enableSave || this.state.formData.registrationNumber.length !== 9) ? 'true' : null}
            >
            Save
            </mosaic-button>
          </div>

        </mosaic-button-group>

      )
    } else {
      return (
        <mosaic-button-group spaced class='float-right'>
          <div className='button-alignment'>

            <mosaic-button
              type='button'
              icon='cancel'
              color='secondary'
              onClick={this.cancelButtonClick}
              className='mr-1'>
            Cancel
            </mosaic-button>
            <mosaic-button
              type='button'
              color='danger'
              icon='delete_sign'
              onClick={this.deleteButtonClick}
            >
            Delete
            </mosaic-button>
            <mosaic-button
              icon='save'
              color='success'
              type='submit'
              className='ml-1'
              submit
              spinner
              disabled={(this.state.isLoading || !this.state.enableSave || this.state.formData.registrationNumber.length !== 9) ? 'true' : null}
            >
            Save
            </mosaic-button>
          </div>

        </mosaic-button-group>
      )
    }
  }

  setup () {
    this.formRef.current.addEventListener('submit', (e) => this.handleSubmit(e))
    this.formRef.current.addEventListener('error', (e) => this.handleErrorSubmit(e))
    this.statusToggle()
    this.inputEvents()
    if (this.obligationFrequencyRef.current) {
      this.obligationFrequencyRef.current.value = this.state.formData.submissionGatewaySettings.frequency
      this.obligationFrequencyRef.current.addEventListener('change', (e) => this.handleSubmissionGatewaySettingsChange(e))
    }
    if (this.submissionGatewayStartDate.current) {
      this.submissionGatewayStartDate.current.addEventListener('change', (e) => this.handleSubmissionGatewaySettingsChange(e))
    }
    this.submissionGatewayRef.current.addEventListener('change', (e) => this.handleSubmissionGatewayChange(e))
  }

  inputEvents = () => {
    this.registerNameRef.current.addEventListener('change', (e) => this.handleChange(e))
    this.registerAddressRef.current.addEventListener('change', (e) => this.handleChange(e))
    this.accountingSchemeRef.current.addEventListener('change', (e) => this.handleChange(e))
  }

  statusToggle = () => {
    this.statusToggleRef.current.checked = this.state.formData.active
    this.statusToggleRef.current.addEventListener('change', (e) => this.handleToggleChange(e))
  }

  deleteModalEvents = () => {
    this.deleteDialogRef.current.addEventListener('submit', (e) => this.deleteTaxRegistration(e))
  }

  handleToggleChange = (e) => {
    const formData = { ...this.state.formData }
    formData[e.target.name] = e.target.checked
    this.statusToggleRef.current.label = e.target.checked === true ? 'Yes' : 'No'
    this.setState({ formData })
  }

  getDeleteModel = () => {
    return (
      <mosaic-form-dialog
        ref={this.deleteDialogRef}
        type='warning'
        confirm-text='Yes'
        dismiss-text='No'
        header-title='Confirm delete'
      >
        <p>Do you want to delete this tax registration?</p>
      </mosaic-form-dialog>
    )
  }

  render () {
    if (this.state.ready) {
      return (
        <React.Fragment>
          <mosaic-form prevent-default ref={this.formRef}>
            <div className='mb-2'>
              <mosaic-number-input
                ref={this.registrationNumberRef}
                name='registrationNumber'
                id = 'registrationNumber'
                class='wd-25'
                block
                onBlur={this.handleChange}
                label='Registration number'
                required
                value={this.state.formData.registrationNumber}
              >
              </mosaic-number-input>
            </div>
            <div className='mb-2'>
              {Array.isArray(this.state.submissionGateways) &&
                <mosaic-select-input
                  label = 'Available gateway'
                  name='SubmissionGatewayId'
                  id='SubmissionGatewayId'
                  ref={this.submissionGatewayRef}
                  value={this.state.formData.SubmissionGatewayId}
                  required
                  no-deselect
                  errorMessage='Please select an available gateway'>
                  <option value=''>Select</option>
                  {this.state.submissionGateways &&
                  this.state.submissionGateways.map(g => <option key={g.id} value={g.id} selected={this.state.formData.SubmissionGatewayId === g.id}>{g.name}</option>)}
                </mosaic-select-input>
              }
            </div>
            {
              this.state.formData.SubmissionGatewayId && this.state.selectedSubmissionGateway && this.state.selectedSubmissionGateway.online === false &&
             <React.Fragment>
               <div className='form-group'>
                 <mosaic-select-input
                   label='Obligation frequency'
                   name='frequency'
                   id='frequency'
                   value={this.state.formData.submissionGatewaySettings.frequency}
                   required
                   no-deselect
                   errorMessage='Please select an available frequency'
                   ref={this.obligationFrequencyRef}
                 >
                   <option value='month'>Monthly</option>
                   <option value='quarter'>Quarterly</option>
                   <option value='biyear'>Bi-Yearly</option>
                   <option value='year'>Yearly</option>
                 </mosaic-select-input>
               </div>
               {
                 this.state.formData.submissionGatewaySettings.frequency &&
                 <div className='form-group'>
                   <mosaic-datetime-picker
                     ref={this.submissionGatewayStartDate}
                     label='Obligation start date'
                     name='startDate'
                     block
                     required
                     date
                     icon='calendar'
                     value={this.state.formData.submissionGatewaySettings.startDate}
                   ></mosaic-datetime-picker>
                 </div>
               }

             </React.Fragment>
            }
            <div className='form-group'>
              <mosaic-text-input
                label='Registered name'
                name='registeredName'
                class='input-class'
                ref={this.registerNameRef}
                value={this.state.formData.registeredName}
                required
              />
            </div>
            <div className='form-group'>
              <mosaic-textarea
                label='Registered address'
                name='registeredAddress'
                ref={this.registerAddressRef}
                value={this.state.formData.registeredAddress}
                multiline
                required
                rows='4'
              />
            </div>
            <div className='form-group'>
              <mosaic-text-input
                name='accountingScheme'
                label='Accounting scheme(s)'
                ref={this.accountingSchemeRef}
                value={this.state.formData.accountingScheme}
              />
              <div className='small pt-1 d-block text-muted'>Use this field to record details of any tax accounting schemes that you use.</div>
            </div>
            <div className='form-group'>
              <p className='font-weight-bold mb-1'>Is your tax registration Active?</p>
              <mosaic-toggle
                color='success'
                ref={this.statusToggleRef}
                label={`${this.state.formData.active ? 'Yes' : 'No'}`}
                id='isActive'
                name='active'
                value={this.state.formData.active}
              />
            </div>
            {
              this.state.displaySubmissionGatewaySettingsConfirm && <ConfirmBox
                header='Confirm delete'
                togConfirm={this.state.showFrequencyConfirm}
                toggleConfirm={this.toggleFrequencyChangeConfirm}
                message='By accepting this message you are confirming that you wish to completely delete all information that is currently entered/attached to this return.'
                rejectFunction={this.rejectFrequencyChangeConfirm}
                acceptFunction={this.acceptFrequencyChangeConfirm}
              />
            }
            {this.getDeleteModel()}

            {this.state.companyError && <DangerAlert error={`${this.state.errorCompanyMsg}`} />}
            {this.state.importError && <DangerAlert error={`${this.state.errorImportMsg}`} />}

            {this.getButtons()}
            {this.getHeaderButton()}
          </mosaic-form>
          {this.state.formData.Companies &&
          <Companies companies={this.state.formData.Companies.slice()} onChange={this.handleCompanyChange} />
          }
        </React.Fragment>
      )
    } else {
      return <Placeholder />
    }
  }
}

TaxRegistrationForm.propTypes = {
  addTaxReg: PropTypes.bool,
  sessionStore: PropTypes.instanceOf(SessionStore),
  taxRegistrationStore: PropTypes.instanceOf(TaxRegistrationStore),
  env: PropTypes.object
}

export { TaxRegistrationForm }
